import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { ReplacementsLibraryListItem } from '@replacements/components/ReplacementsLibrary/List/ReplacementsLibraryList';
import {
  ExtendedPhoneticLibraryType,
  ReplacementsLibraryStore,
  useReplacementsLibraryStore,
} from '@replacements/store/useReplacementsLibraryStore';
import { LibraryToolbarDrawerContentType } from '@replacements/types/replacementLibraryTypes';
import { PhoneticLibraryType } from '../../../../server/graphql/__generated.types';

interface ReplacementsLibraryContextProps extends ReplacementsLibraryStore {}

const getInitialState = (): ReplacementsLibraryContextProps => ({
  activeType: PhoneticLibraryType.Personal,
  library: [],
  drawerContentType: LibraryToolbarDrawerContentType.Default,
  setActiveType: (_activeType: ExtendedPhoneticLibraryType) => {},
  initLibrary: () => {},
  expandWord: () => {},
  addWord: () => {},
  addReplacement: () => {},
  selectReplacement: () => {},
  clearSelectedReplacement: () => {},
  selectWord: () => {},
  getSelectedWord: () => '',
  getSelectedReplacement: () => undefined,
  updateDrawerContentType: () => {},
  deleteReplacement: () => LibraryToolbarDrawerContentType.Default,
  updateReplacement: () => {},
  deleteWord: () => {},
  collapseWords: () => {},
  setDefaultReplacementOriginalWord: (
    _defaultReplacementOriginalWord: string
  ) => {},
  defaultReplacementOriginalWord: '',
  isNewReplacementSidebarAccordionOpen: false,
  setIsNewReplacementSidebarAccordionOpen: (_isOpen: boolean) => {},
  shouldFocusReplacement: false,
  setShouldFocusReplacement: _shouldFocusReplacement => {},
});

const ReplacementsLibraryContext =
  createContext<ReplacementsLibraryContextProps>(getInitialState());

interface ReplacementLibraryProviderProps {
  library: ReplacementsLibraryListItem[];
  children: React.ReactNode;
  /**
   * Opt out of this library synchronization in favor of callers invalidating it.
   */
  skip?: boolean;
}

// A "provider" is used to encapsulate only the
// components that needs the state in this context
/**
 * @todo consider a full deprecation of this provider in favor a a new hook.
 */
export const ReplacementsLibraryProvider = ({
  library,
  children,
  skip,
}: ReplacementLibraryProviderProps) => {
  const [
    items,
    activeType,
    drawerContentType,
    initLibrary,
    setActiveType,
    expandWord,
    addWord,
    addReplacement,
    selectReplacement,
    clearSelectedReplacement,
    selectWord,
    getSelectedWord,
    getSelectedReplacement,
    updateDrawerContentType,
    deleteReplacement,
    updateReplacement,
    deleteWord,
    collapseWords,
    defaultReplacementOriginalWord,
    setDefaultReplacementOriginalWord,
    isNewReplacementSidebarAccordionOpen,
    setIsNewReplacementSidebarAccordionOpen,
    shouldFocusReplacement,
    setShouldFocusReplacement,
  ] = useReplacementsLibraryStore(state => [
    state.library,
    state.activeType,
    state.drawerContentType,
    state.initLibrary,
    state.setActiveType,
    state.expandWord,
    state.addWord,
    state.addReplacement,
    state.selectReplacement,
    state.clearSelectedReplacement,
    state.selectWord,
    state.getSelectedWord,
    state.getSelectedReplacement,
    state.updateDrawerContentType,
    state.deleteReplacement,
    state.updateReplacement,
    state.deleteWord,
    state.collapseWords,
    state.defaultReplacementOriginalWord,
    state.setDefaultReplacementOriginalWord,
    state.isNewReplacementSidebarAccordionOpen,
    state.setIsNewReplacementSidebarAccordionOpen,
    state.shouldFocusReplacement,
    state.setShouldFocusReplacement,
  ]);

  /**
   * Not sure if there's a better/easier way to init zustand store from the start
   * or we could possibly move this to parent? idk if zustand would still work
   * between parent/child or if we need provider at that point
   */
  const prevLibraryRef = useRef<string>();

  useEffect(() => {
    // bypass this in favor of callers
    if (skip) {
      return;
    }
    const stringifiedLibrary = JSON.stringify(library);
    if (prevLibraryRef.current !== stringifiedLibrary) {
      initLibrary(library);
      prevLibraryRef.current = stringifiedLibrary;
    }
  }, [library, initLibrary, skip]);

  const value = useMemo(
    () => ({
      library: items,
      activeType,
      drawerContentType,
      initLibrary,
      setActiveType,
      expandWord,
      addWord,
      addReplacement,
      selectReplacement,
      clearSelectedReplacement,
      selectWord,
      getSelectedWord,
      getSelectedReplacement,
      updateDrawerContentType,
      deleteReplacement,
      updateReplacement,
      deleteWord,
      collapseWords,
      defaultReplacementOriginalWord,
      setDefaultReplacementOriginalWord,
      isNewReplacementSidebarAccordionOpen,
      setIsNewReplacementSidebarAccordionOpen,
      shouldFocusReplacement,
      setShouldFocusReplacement,
    }),
    [
      items,
      activeType,
      drawerContentType,
      initLibrary,
      setActiveType,
      expandWord,
      addWord,
      addReplacement,
      selectReplacement,
      clearSelectedReplacement,
      selectWord,
      getSelectedWord,
      getSelectedReplacement,
      updateDrawerContentType,
      deleteReplacement,
      updateReplacement,
      deleteWord,
      collapseWords,
      defaultReplacementOriginalWord,
      setDefaultReplacementOriginalWord,
      isNewReplacementSidebarAccordionOpen,
      setIsNewReplacementSidebarAccordionOpen,
      shouldFocusReplacement,
      setShouldFocusReplacement,
    ]
  );

  return (
    <ReplacementsLibraryContext.Provider value={value}>
      {children}
    </ReplacementsLibraryContext.Provider>
  );
};

export const useReplacementsLibraryContext = () =>
  useContext(ReplacementsLibraryContext);

export default ReplacementsLibraryContext;
